"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState, useCallback, useRef } from "react";
import { ImageIcon, ZoomIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import DressModal from "@/components/home/DressModal";
import { logger } from "@/utils/logger";

interface CompletedDressesSectionProps {
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaHref?: string;
  className?: string;
  initialData?: Array<{
    id: string;
    url: string;
    number: string;
    alt: string;
  }>;
}

export default function CompletedDressesSection({
  title = "See Our Work",
  subtitle = "Real dresses crafted by our professional seamstresses",
  ctaText = "Create Your Custom Dress",
  ctaHref = "/create",
  className = "",
  initialData = [],
}: CompletedDressesSectionProps) {
  // State for completed dress images
  const [completedDresses, setCompletedDresses] = useState<
    Array<{
      id: string;
      url: string;
      number: string;
      alt: string;
    }>
  >(initialData);
  const [loadingCompletedDresses, setLoadingCompletedDresses] =
    useState<boolean>(initialData.length === 0);
  // Defer fetching/rendering until the section is visible
  const [hasEnteredViewport, setHasEnteredViewport] = useState<boolean>(false);
  const sectionRef = useRef<HTMLElement | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedDress, setSelectedDress] = useState<{
    id: string;
    url: string;
    number: string;
    alt: string;
  } | null>(null);

  // Observe section visibility to defer work until in-view
  useEffect(() => {
    if (hasEnteredViewport) return;
    const target = sectionRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setHasEnteredViewport(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "0px", threshold: 0.1 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasEnteredViewport]);

  // Fetch completed dress images once visible (unless provided via initialData)
  useEffect(() => {
    if (initialData.length > 0) return;
    if (!hasEnteredViewport) return;

    let isMounted = true;
    const fetchCompletedDresses = async () => {
      try {
        setLoadingCompletedDresses(true);
        const res = await fetch("/api/completed-dresses");
        if (!res.ok) return;
        const data = await res.json();
        if (isMounted && data.success) {
          setCompletedDresses(data.images || []);
        }
      } catch (error) {
        logger.error(
          "Failed to fetch completed dresses",
          {
            component: "CompletedDressesSection",
          },
          error instanceof Error ? error : new Error(String(error))
        );
      } finally {
        if (isMounted) setLoadingCompletedDresses(false);
      }
    };

    fetchCompletedDresses();
    return () => {
      isMounted = false;
    };
  }, [hasEnteredViewport, initialData.length]);

  // Modal handlers
  const handleOpenModal = useCallback(
    (dress: { id: string; url: string; number: string; alt: string }) => {
      setSelectedDress(dress);
      setIsModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedDress(null);
  }, []);

  return (
    <>
      <section
        className={`bg-background py-16 sm:py-24 ${className}`}
        ref={sectionRef}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 sm:mb-6">
              {title}
            </h2>
            {subtitle && (
              <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
                {subtitle}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {!hasEnteredViewport
              ? // Defer heavy rendering until visible: show lightweight placeholders
                Array.from({ length: 8 }).map((_, index) => (
                  <Card
                    key={`pre-skeleton-${index}`}
                    variant="default"
                    padding="none"
                    overflowHidden
                    className="relative aspect-[3/4] rounded-xl"
                  >
                    <div className="absolute inset-0 bg-muted" />
                  </Card>
                ))
              : loadingCompletedDresses
                ? // Loading skeleton
                  Array.from({ length: 8 }).map((_, index) => (
                    <Card
                      key={`skeleton-${index}`}
                      variant="default"
                      padding="none"
                      overflowHidden
                      className="relative aspect-[3/4] rounded-xl"
                    >
                      <div className="absolute inset-0 bg-muted animate-pulse flex items-center justify-center">
                        <div className="text-center p-4">
                          <ImageIcon
                            className="w-12 h-12 text-muted-foreground mx-auto mb-2"
                            aria-hidden
                          />
                          <p className="text-sm text-muted-foreground font-medium">
                            Loading...
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))
                : // Real completed dress images
                  completedDresses.map(dress => (
                    <Card
                      key={dress.id}
                      variant="interactive"
                      padding="none"
                      overflowHidden
                      className="relative aspect-[3/4] rounded-xl cursor-pointer group"
                      onClick={() => handleOpenModal(dress)}
                      role="button"
                      tabIndex={0}
                      aria-label={`View full size image of ${dress.alt}`}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleOpenModal(dress);
                        }
                      }}
                    >
                      <Image
                        src={dress.url}
                        alt={dress.alt}
                        width={256}
                        height={341}
                        className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                        sizes="(max-width: 640px) 45vw, (max-width: 768px) 30vw, (max-width: 1024px) 22vw, 256px"
                        loading="lazy"
                        fetchPriority="low"
                        quality={75}
                        placeholder="blur"
                        blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                      />
                      {/* Overlay with dress number */}
                      <div className="absolute top-2 right-2 bg-foreground/70 text-primary-foreground text-xs px-2 py-1 rounded-full font-medium">
                        #{dress.number}
                      </div>
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-card/90 rounded-full p-3">
                          <ZoomIn
                            className="w-6 h-6 text-muted-foreground dark:text-foreground"
                            aria-hidden
                          />
                        </div>
                      </div>
                    </Card>
                  ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button asChild size="lg">
              <Link href={ctaHref}>{ctaText}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Dress Modal */}
      <DressModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        dress={selectedDress}
      />
    </>
  );
}
